import { useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { newId } from '@gk/contracts'
import { useClub } from '../club/ClubProvider.tsx'
import { useAvailability, useDefectViews, useWorkOrders, OPEN_WORK_ORDER_STATES, type Blocker, type DefectView, type Severity } from '../data/safety.ts'
import type { AssetRow } from '../data/fleet.ts'
import { ASSET_SECTIONS } from '../data/workshopUi.ts'
import { formatLocal } from '../lib/time.ts'
import { Banner, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction, useWriteAccess } from '../ui/hooks.ts'
import type { Pending } from '../sync/merge.ts'
import { WorkOrderPanel } from './WorkOrders.tsx'
import { ReleasePanel } from './Release.tsx'
import { useCloseOnPending } from './workshop/shared.tsx'

export const SEVERITY_TONE: Record<Severity, 'gray' | 'amber' | 'red'> = { minor: 'gray', major: 'amber', critical: 'red' }

/** Why a machine is out of service, from the device's own data (so it is true with no signal). The app records the state; it never stops a
 *  machine, and says so: the key comes out and the machine is tagged. */
export function AvailabilityBanner({ assetId }: { assetId: string }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const { blockers } = useAvailability()
  const list = blockers(assetId)
  if (list.length === 0) return null
  const when = (b: Blocker) => formatLocal(b.restriction.started_at, site.timezone, 'dd/MM/yyyy HH:mm')
  const reason = (b: Blocker): string => {
    const text = b.restriction.reason_kind === 'critical_defect' ? (b.defect?.description ?? '') : (b.restriction.note ?? '')
    const base = t(`safety.reason.${b.restriction.reason_kind}`, { text: text || t('safety.reason.noText') })
    return b.own ? base : `${base} (${t('safety.reason.via', { tag: b.via?.readable_tag ?? '…' })})`
  }
  return (
    <div role="alert" className="flex flex-col gap-2 rounded-xl border-2 border-red-500 bg-red-50 p-4 text-red-950 dark:border-red-600 dark:bg-red-950/40 dark:text-red-100" data-testid="out-of-service">
      <p className="text-xl font-bold">{t('safety.outTitle')}</p>
      <ul className="flex flex-col gap-1 text-sm" data-testid="out-reasons">
        {list.map((b) => <li key={b.restriction.id}>{reason(b)} · <span className="opacity-75">{t('common.since', { when: when(b) })}</span></li>)}
      </ul>
      <p className="text-sm font-semibold">{t('safety.isolate')}</p>
    </div>
  )
}

export function OutOfServicePill({ assetId }: { assetId: string }) {
  const { t } = useTranslation()
  const { isBlocked } = useAvailability()
  return isBlocked(assetId) ? <Pill tone="red">{t('safety.outOfService')}</Pill> : null
}

/** The three things a person does with a machine (M23 item 22): work with it, the pre-start check, a defect. Full-width 56-px buttons; the
 *  jardinier's page puts them first, the mechanic's at the top of the safety section. One word, *machine*. */
export function SafetyActions({ asset }: { asset: Pending<AssetRow> }) {
  const { t } = useTranslation()
  const exec = useWriteAccess('job.execute')
  const check = useWriteAccess('inspection.submit')
  const report = useWriteAccess('defect.report')
  if (asset.lifecycle === 'retired') return null
  const machine = asset.kind === 'machine' || asset.kind === 'vehicle'
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="asset-actions">
      {exec.permitted && machine && <Link to="/work/start" search={{ asset: asset.id }} className="btn btn-primary w-full" data-testid="start-work">{t('assets.workWith')}</Link>}
      {check.permitted && <Link to="/assets/$assetId/check" params={{ assetId: asset.id }} className={`btn w-full ${exec.permitted && machine ? '' : 'btn-primary'}`} data-testid="start-check">{t('check.start')}</Link>}
      {report.permitted && <Link to="/assets/$assetId/defect" params={{ assetId: asset.id }} className="btn w-full" data-testid="report-defect">{t('defect.report')}</Link>}
    </div>
  )
}

/** Everything about one machine's safety: its defects, its work orders, the release; with `actions`, the three buttons at its top (the office's
 *  page); the jardinier's page shows them under the tag instead (M23 item 22). */
export function SafetyPanel({ asset, actions = true }: { asset: Pending<AssetRow>; actions?: boolean }) {
  const { t } = useTranslation()
  const views = useDefectViews()
  const mine = useMemo(() => (views ?? []).filter((v) => v.defect.asset_id === asset.id), [views, asset.id])
  const open = mine.filter((v) => v.status !== 'resolved').sort((a, b) => (a.defect.reported_at < b.defect.reported_at ? 1 : -1))
  const shownResolved = mine.filter((v) => v.status === 'resolved').length
  return (
    <section id={ASSET_SECTIONS.safety} className="flex scroll-mt-20 flex-col gap-4" data-testid="safety-panel">
      <h2 className="text-xl font-bold">{t('safety.panelTitle')}</h2>
      {actions && <SafetyActions asset={asset} />}
      <h3 className="text-lg font-bold">{t('defect.openTitle')}</h3>
      {open.length === 0 ? <p className="opacity-70" data-testid="no-defects">{t('defect.none')}</p> : (
        <ul className="flex flex-col gap-2" data-testid="defect-list">
          {open.map((v) => <DefectCard key={v.defect.id} view={v} assetId={asset.id} />)}
        </ul>
      )}
      {shownResolved > 0 && <p className="meta">{t('defect.resolvedCount', { count: shownResolved })}</p>}
      <WorkOrderPanel asset={asset} />
      <ReleasePanel asset={asset} />
    </section>
  )
}

function DefectCard({ view, assetId }: { view: DefectView; assetId: string }) {
  const { t } = useTranslation()
  const { site } = useClub()
  const triage = useWriteAccess('defect.triage')
  const orders = useWorkOrders()
  const [openForm, setOpenForm] = useState(false)
  const [kept, setKept] = useState(false)                      // the triage was kept on the device (M23 item 10)
  const d = view.defect
  const mineOrders = (orders ?? []).filter((w) => w.asset_id === assetId && OPEN_WORK_ORDER_STATES.includes(w.state) && !view.workOrderIds.includes(w.id))
  return (
    <li className="card flex flex-col gap-2" data-testid="defect-row">
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={SEVERITY_TONE[view.severity]}>{t(`defect.severity.${view.severity}`)}</Pill>
        {view.status === 'repaired' && <Pill tone="green">{t('defect.repaired')}</Pill>}
        {d._pending && <Pill tone="amber">{t('assets.pending')}</Pill>}
        {d.attribution_unverified && <Pill tone="amber">{t('defect.unverified')}</Pill>}
      </div>
      <p className="font-semibold">{d.description}</p>
      <p className="meta">{t('defect.reportedAt', { when: formatLocal(d.reported_at, site.timezone, 'dd/MM/yyyy HH:mm') })}</p>
      {kept && <PendingBanner />}
      {triage.allowed && !d._pending && (openForm
        ? <TriageForm view={view} orders={mineOrders.map((w) => ({ id: w.id, label: w.complaint ?? t(`workorder.kind.${w.kind}`) }))} onDone={() => setOpenForm(false)} onPending={() => { setKept(true); setOpenForm(false) }} />
        : <button className="btn !min-h-11" onClick={() => { setKept(false); setOpenForm(true) }} data-testid="triage-open">{t('defect.triage')}</button>)}
    </li>
  )
}

const RANK: Record<Severity, number> = { minor: 1, major: 2, critical: 3 }

/** The mechanic's triage: note it, link it to a work order, raise its severity. Lowering it is a separate, stronger action (it needs a fresh
 *  second-factor code), so it is not offered here. */
function TriageForm({ view, orders, onDone, onPending }: { view: DefectView; orders: Array<{ id: string; label: string }>; onDone: () => void; onPending: () => void }) {
  const { t } = useTranslation()
  const w = useWorkshopAction()
  const eventId = useRef(newId())
  const [severity, setSeverity] = useState<'' | Severity>('')
  const [wo, setWo] = useState('')
  const [note, setNote] = useState('')
  const higher = (['minor', 'major', 'critical'] as const).filter((s) => RANK[s] > RANK[view.severity])
  useCloseOnPending(w.pending, onPending)
  const submit = (e: FormEvent) => {
    e.preventDefault()
    void w.run('defect.triage@1', {
      event_id: eventId.current, defect_id: view.defect.id, ...(severity ? { severity } : {}), ...(wo ? { work_order_id: wo } : {}), ...(note.trim() ? { note: note.trim() } : {}),
    }, 'defect.triaged').then((ok) => { if (ok) { eventId.current = newId(); onDone() } })
  }
  return (
    <form onSubmit={submit} className="flex flex-col gap-3" data-testid="triage-form">
      {higher.length > 0 && (
        <Field label={t('defect.raiseTo')} htmlFor={`sev-${view.defect.id}`}>
          <select id={`sev-${view.defect.id}`} className="input" value={severity} onChange={(e) => setSeverity(e.target.value as Severity | '')}>
            <option value="">{t('defect.keepSeverity')}</option>
            {higher.map((s) => <option key={s} value={s}>{t(`defect.severity.${s}`)}</option>)}
          </select>
        </Field>
      )}
      {orders.length > 0 && (
        <Field label={t('defect.linkOrder')} htmlFor={`wo-${view.defect.id}`}>
          <select id={`wo-${view.defect.id}`} className="input" value={wo} onChange={(e) => setWo(e.target.value)}>
            <option value="">{t('mounts.choose')}</option>
            {orders.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
        </Field>
      )}
      <Field label={t('defect.triageNote')} htmlFor={`note-${view.defect.id}`}>
        <input id={`note-${view.defect.id}`} className="input" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
      </Field>
      {w.error && <Banner tone="error">{w.error}</Banner>}
      <div className="flex gap-2">
        <button className="btn btn-primary flex-1" disabled={w.busy} data-testid="triage-go">{t('common.save')}</button>
        <button type="button" className="btn" onClick={onDone}>{t('common.cancel')}</button>
      </div>
    </form>
  )
}
